import { Attr, Names, Values } from '@mode-switch/model';
import { EncField, EncSeries, Encoding } from '@mode-vegas/vegas';
import { ChartFacade } from '@mode/charts/contract';
import { EncodingHelpers, RenderDataModel } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { OrgTypes } from '@mode/shared/contract-common';
import { DEFAULT_CHART_FORMAT, HashKey, isPresent } from '@mode/shared/util-js';
import { CompiledField } from '@mode/shared/util-switch';
import { AsyncSubject, filter } from 'rxjs';
import { GradientRangeValues } from './colors-modal.component';

/**
 * Gets the total domain of values for the color field. Returns an array containing
 * the domain. For discrete fields, this will be the unique values. For continuous
 * fields, the array will contain two elements: the minimum and maximum value.
 */
export function resolveColorDomain(
  encoding: Encoding,
  renderModel: RenderDataModel,
  view: FlamingoTypes.ModeView,
  field: CompiledField
) {
  // If the field is Measure Names, there is special handling to get the names.
  if (field.formula === Names.Key) {
    return resolveMeasureNamesColorDomain(encoding, view);
  }

  // If the field is in 'value', we need to calculate the domain
  // using Measure Names and Measure Values
  if (encoding.value.some((f) => f.id === field.alias)) {
    return resolveMeasureValueColorDomain(encoding, renderModel, field);
  }
  const series =
    encoding.marks.channel === '*'
      ? []
      : encoding.marks.series.reduce((seriesIds, series) => {
          if (series.properties.color.values[0]?.formula === field.formula) {
            seriesIds.push(series.field);
          }
          return seriesIds;
        }, [] as Array<string>);
  if (field.isDiscrete) {
    return renderModel.getColorDomain(encoding, DEFAULT_CHART_FORMAT, series[0], false);
  }
  // For continuous fields, we need to merge the ranges from all series

  const calculateMinMax = (domain: any[], current: { min: number; max: number }) => {
    return domain.reduce((domain, value) => {
      if (value == null) {
        return domain;
      }
      let { min, max } = domain;
      if (Number.isNaN(min) || value < min) {
        min = value;
      }
      if (Number.isNaN(max) || value > max) {
        max = value;
      }
      return { min, max };
    }, current);
  };

  let minMax = { min: Number.NaN, max: Number.NaN };
  if (encoding.marks.channel === '*') {
    const domain = renderModel.getColorDomain(encoding, DEFAULT_CHART_FORMAT, undefined, false);
    minMax = calculateMinMax(domain, minMax);
  } else {
    minMax = series.reduce((range, seriesField) => {
      const domain = renderModel.getColorDomain(encoding, DEFAULT_CHART_FORMAT, seriesField, false);
      return calculateMinMax(domain, range);
    }, minMax);
  }
  return [minMax.min, minMax.max];
}

/**
 * Retrieves the color domain for the specified color field. For discrete, this will return
 * the domain of all values. For continuous, this will return the min and max values aggregated
 * based on the level of detail in the encoding.
 *
 * Use 'isContinuousUnaggregated' for a continuous field where the result should not be aggregated.
 * This is typically for a flat table.
 *
 * In all cases, the retrieved domain will honor all of the filters in the encoding.
 */
export function fetchColorDomain(
  chartFacade: ChartFacade,
  colorField: CompiledField,
  view: FlamingoTypes.ModeView,
  encoding: Encoding,
  queryRunToken: string,
  vizToken: string,
  isContinuousUnaggregated: boolean
) {
  return colorField.isDiscrete || isContinuousUnaggregated
    ? fetchDiscreteColorDomain(chartFacade, colorField, view, encoding, queryRunToken)
    : fetchContinuousColorDomain(chartFacade, colorField, view, encoding, queryRunToken, vizToken);
}

function fetchDiscreteColorDomain(
  chartFacade: ChartFacade,
  colorField: CompiledField,
  view: FlamingoTypes.ModeView,
  encoding: Encoding,
  queryRunToken: string
) {
  // Need a unique alias for this field. See filter-modal-helpers.ts
  const domainAlias = `D${HashKey.hashFn(colorField.displayFormula ?? '')
    .toString(16)
    .padStart(8, '0')}`;
  const refId = `color-discrete-domain-query`;

  // Get the series associated with the color field.
  const seriesId =
    encoding.marks.channel === '*'
      ? '*'
      : (encoding.marks.series.find((series) => series.properties.color.values.some((f) => f.id === colorField.alias))
          ?.field ?? '');

  const asyncSubject = new AsyncSubject<Array<any>>();

  chartFacade.fetchColorDomainValues(queryRunToken, view, encoding, colorField, refId, domainAlias, seriesId);

  const querySubscription = chartFacade
    .createSelectDomainValues(refId, domainAlias ?? '')
    .pipe()
    .subscribe((result) => {
      if (result?.type === Attr.Type.Discrete) {
        const values = [...result.values];
        asyncSubject.next(values);
        asyncSubject.complete();
      } else if (result?.type === Attr.Type.Continuous) {
        const values = [result.min, result.max];
        asyncSubject.next(values);
        asyncSubject.complete();
      }

      errorSubscription.unsubscribe();
      querySubscription.unsubscribe();
    });

  const errorSubscription = chartFacade
    .createErrorFromDomainQuery(refId)
    .pipe(filter(isPresent))
    .subscribe((error) => {
      asyncSubject.error(error);
      asyncSubject.complete();
      errorSubscription.unsubscribe();
      querySubscription.unsubscribe();
    });

  return asyncSubject.asObservable();
}

function fetchContinuousColorDomain(
  chartFacade: ChartFacade,
  colorField: CompiledField,
  view: FlamingoTypes.ModeView,
  encoding: Encoding,
  queryRunToken: string,
  vizToken: string
) {
  const refId = `color-discrete-domain-query`;

  const isColorInValues = encoding.value.some((v) => v.id === colorField.alias);
  const colorFields =
    encoding.marks.channel !== '*'
      ? encoding.marks.series.reduce((matches, series) => {
          if (
            series.properties.color.values[0]?.formula === colorField?.formula ||
            (series.properties.color.values[0]?.formula === Values.Key && isColorInValues)
          ) {
            matches.push(series.properties.color.values[0]?.id ?? '');
          }
          return matches;
        }, [] as string[])
      : encoding.marks.properties.color.values.map((field) => field.id ?? '');

  const asyncSubject = new AsyncSubject<number[]>();

  chartFacade.fetchContinuousMarkDomainRange(queryRunToken, view, encoding, refId, colorField, 'color');

  const querySubscription = chartFacade
    .createSelectContinuousMarkDomainRange(vizToken, refId, colorFields)
    .pipe()
    .subscribe((result) => {
      const values = [result.min, result.max];
      asyncSubject.next(values);
      asyncSubject.complete();
      errorSubscription.unsubscribe();
      querySubscription.unsubscribe();
    });

  const errorSubscription = chartFacade
    .createErrorFromDomainQuery(refId)
    .pipe(filter(isPresent))
    .subscribe((error) => {
      asyncSubject.error(error);
      asyncSubject.complete();
      errorSubscription.unsubscribe();
      querySubscription.unsubscribe();
    });

  return asyncSubject.asObservable();
}

export function getColorCompiledFields(encoding: Encoding, view: FlamingoTypes.ModeView, isSplitLegend: boolean) {
  let colorFields = EncodingHelpers.getUniqueEncFieldsByMarkChannel(encoding, 'color');
  // If we are splitting the legend and we have Measure Values, then replace Measure Values with
  // the fields in 'value'.
  if (isSplitLegend && colorFields.some((f) => f.formula === Values.Key) && encoding.value.length > 0) {
    // Remove Measure Values
    colorFields = colorFields.filter((f) => f.formula !== Values.Key);
    // Dedup the values by formula
    const valuesMap = new Map(encoding.value.map((f) => [f.formula, f]));
    // Append the values
    colorFields = colorFields.concat(Array.from(valuesMap.values()));
  }
  // Return the fields as CompiledFields sorted by display formula
  return colorFields
    .map((encField) => CompiledField.fromEncField(view, encField))
    .sort((a, b) => a.displayFormula.localeCompare(b.displayFormula));
}

export function isFlatTableEncoding(colorField: CompiledField, encoding: Encoding | undefined) {
  if (encoding === undefined) {
    return false;
  }
  // This is a flat table if the specified color field is in text
  const textFields = EncodingHelpers.getUniqueEncFieldsByMarkChannel(encoding, 'text');
  return textFields.some((f) => f.id === colorField.alias);
}

//------------------------------------------------------------------
// NOTE: THE FOLLOWING TWO FUNCTIONS WERE COPIED FROM MARKS-CARD
// COMPONENT. ONCE WE SWITCH TO THE NEW COLOR MODAL, THESE FUNCTIONS
// SHOULD BE REMOVED FROM marks-card.component.ts.
// -----------------------------------------------------------------

// This function gets all of the measure names in the encoding that could be used
// for color. In some cases, it is difficult to determine exactly which measures will
// have color applied. This function errs on the side of returning too many measures.
// Any extra measures which get color assigned will be ignored anyway. This is better
// than having too few measures as the user would be unable to configure those measures.
function resolveMeasureNamesColorDomain(encoding: Encoding, view: FlamingoTypes.ModeView) {
  let measures: Array<EncField> = [];
  const hasMeasureValues = EncodingHelpers.isInEncoding(encoding, Values.Key);
  if (hasMeasureValues && encoding.marks.channel === '*') {
    // Table case is only concerned with the Measure Value fields
    measures = [...encoding.value];
  } else if (encoding.marks.channel !== '*') {
    // Use the measure from each series. If one of the series measures is
    // Measure Values, then also include the value fields. Also, if one of
    // the series has Measure Values in a marks channel, then also include
    // the value fields. Note, measures may include duplicates. This is okay
    // as they are de-duped before being passed to the modal.
    const seriesFields =
      encoding.marks.channel === 'y' ? [...encoding.y, ...encoding.y2] : [...encoding.x, ...encoding.x2];

    measures = encoding.marks.series.reduce(
      (all, series) => {
        const field = seriesFields.find((field: EncField) => field.id === series.field);
        if (field) {
          if (field.formula === Values.Key) {
            encoding.value.forEach((valueField) => all.push(valueField));
          } else {
            all.push(field);
            if (seriesHasMeasureValuesOnMark(series)) {
              encoding.value.forEach((valueField) => all.push(valueField));
            }
          }
        }
        return all;
      },
      [] as Array<Readonly<EncField>>
    );
  }
  return view != null ? measures.map((encField) => CompiledField.fromEncField(view, encField).displayName) : [];
}

/**
 * This function assumes the case of separate legends with Measure Values on color
 * and Measure Names somewhere in the encoding. If the encoding does not match
 * these expectations, this will return [NaN, NaN].
 */
function resolveMeasureValueColorDomain(encoding: Encoding, renderModel: RenderDataModel, field: CompiledField) {
  // This function assumes that we are in a split legend case. It is the only situation
  // where the field is in Value rather than on color.
  const seriesFields = encoding.marks.channel === '*' ? ['*'] : encoding.marks.series.map((s) => s.field);

  // Go through all of the series (or discrete marks for '*').
  // Check if this series has MV on color and MN in the (series) encoding.
  // If it does, then go through the data to find the min/max.
  // Otherwise, skip the series.
  let minMax = { min: Number.NaN, max: Number.NaN };
  seriesFields.forEach((seriesField) => {
    // Get all of the fields in the series and see if Measure Names is there.
    const allSeriesFields = EncodingHelpers.collectEncFields(encoding, seriesField);
    const measureNamesField = allSeriesFields.find((f) => f.formula === Names.Key);
    if (measureNamesField === undefined) {
      return;
    }
    // Check if Measure Values is on the color channel
    const series = encoding.marks.series.find((s) => s.field === seriesField);
    const measureValuesField =
      seriesField === '*' ? encoding.marks.properties.color.values[0] : series?.properties.color.values[0];
    if (measureValuesField?.formula !== Values.Key) {
      return;
    }
    // This series meets the expectations. Use the data for the series
    // from the RenderDataModal.
    const seriesData = renderModel.getSeriesData(seriesField);
    if (seriesData?.length > 0) {
      minMax = seriesData.reduce<{ min: number; max: number }>((domain, item) => {
        if (item == null) {
          return domain;
        }
        // There could be multiple measures. Skip if the Measure Name
        // doesn't match the measure we are looking for.
        if (item[Names.Key] !== field.displayName) {
          return domain;
        }
        // The value we need is in the data under the MV field id
        const value = item[Values.Key];
        if (value == null) {
          return domain;
        }

        let { min, max } = domain;
        if (Number.isNaN(min) || value < min) {
          min = value;
        }
        if (Number.isNaN(max) || value > max) {
          max = value;
        }
        return { min, max };
      }, minMax);
    }
  });
  return [minMax.min, minMax.max];
}

// Determines if the series includes Measure Values on any of its marks channels.
function seriesHasMeasureValuesOnMark(series: EncSeries) {
  const allValues = [
    ...series.properties.angle.values,
    ...series.properties.color.values,
    ...series.properties.detail.values,
    ...series.properties.size.values,
    ...series.properties.text.values,
    ...series.properties.label.values,
  ];
  return allValues.some((encField) => encField.formula === Values.Key);
}

export function makeContinuousGradient(
  gradientType: ChartTypes.GradientType.DIVERGING | ChartTypes.GradientType.SEQUENTIAL,
  colors: string[],
  range?: GradientRangeValues,
  stepped = false,
  numSteps = 0
): ChartTypes.ContinuousGradient {
  const colorValues = gradientType === ChartTypes.GradientType.DIVERGING ? colors.slice(0, 3) : colors.slice(0, 2);
  const colorEntries = makeColorGradientEntries(
    colorValues,
    range ?? { min: undefined, mid: undefined, max: undefined }
  );

  return {
    gradientType,
    colorEntries,
    stepped,
    numSteps,
  };
}

/**
 * Currently assumes either 2 (sequential) or 3 (divergent) colors.
 * Also, assumes order:
 * [min, max] for sequential
 * [min, mid, max] for divergent
 * @param colors
 * @returns GradientColorEntries
 */
function makeColorGradientEntries(colors: string[], range: GradientRangeValues) {
  if (colors.length < 2 || colors.length > 3) {
    throw Error(`Invalid number of colors: ${colors.length}. Expect 2 (sequential) or 3 (diverging)`);
  }
  const entries = colors.reduce((gradient, color, index, all) => {
    if (index == 0) {
      gradient[ChartTypes.GradientStep.DATA_MIN] = color;
    } else if (index === all.length - 1) {
      gradient[ChartTypes.GradientStep.DATA_MAX] = color;
    } else {
      gradient[ChartTypes.GradientStep.DATA_MIDPOINT] = color;
    }
    return gradient;
  }, {} as ChartTypes.GradientColorEntries);
  // Replace the default steps with any specific step values
  const replaceStep = (step: ChartTypes.GradientStep, value: number | undefined) => {
    if (value != null) {
      const color = entries[step];
      entries[value] = color;
      delete entries[step];
    }
  };
  replaceStep(ChartTypes.GradientStep.DATA_MIN, range.min);
  replaceStep(ChartTypes.GradientStep.DATA_MAX, range.max);
  replaceStep(ChartTypes.GradientStep.DATA_MIDPOINT, range.mid);
  return entries;
}

export function getCurrentGradientRange(gradient: ChartTypes.ContinuousGradient | undefined) {
  if (gradient == null) {
    return { min: undefined, mid: undefined, max: undefined };
  }

  const numericKeys = Object.keys(gradient.colorEntries)
    .reduce((keys, key) => {
      if (
        key !== ChartTypes.GradientStep.DATA_MIN &&
        key !== ChartTypes.GradientStep.DATA_MAX &&
        key !== ChartTypes.GradientStep.DATA_MIDPOINT
      ) {
        keys.push(+key);
      }
      return keys;
    }, [] as number[])
    .sort((a, b) => a - b);

  const min = gradient.colorEntries[ChartTypes.GradientStep.DATA_MIN] === undefined ? numericKeys.shift() : undefined;

  const mid =
    Object.keys(gradient.colorEntries).length === 3 &&
    gradient.colorEntries[ChartTypes.GradientStep.DATA_MIDPOINT] === undefined
      ? numericKeys.shift()
      : undefined;

  const max = gradient.colorEntries[ChartTypes.GradientStep.DATA_MAX] === undefined ? numericKeys.shift() : undefined;
  return { min, mid, max };
}

export function getDefaultPalette(
  field: CompiledField,
  orgPalettes: ReadonlyArray<Readonly<OrgTypes.ColorPalette>>,
  paletteToken?: string
) {
  // If token is passed, try to find the corresponding palette.
  // Only use that palette if it is an appropriate type for the field type.
  // Some continuous fields have previously been saved with categorical palettes.
  // In that case, we want to use the default sequential palette instead.
  const requestedPalette = orgPalettes.find((palette) => palette.token === paletteToken);

  let defaultPaletteType = field.isContinuous
    ? OrgTypes.ColorPaletteType.Sequential
    : OrgTypes.ColorPaletteType.Categorical;

  if (requestedPalette?.type === OrgTypes.ColorPaletteType.Divergent && field.isContinuous) {
    defaultPaletteType = OrgTypes.ColorPaletteType.Divergent;
  }

  // Filter to specific types
  const palettes = orgPalettes.filter((palette) => palette.type === defaultPaletteType);

  // Try to find by paletteToken if provided from the appropriate types
  let defaultPalette = paletteToken ? palettes.find((palette) => palette.token === paletteToken) : undefined;

  // If not found, get the default for type
  if (defaultPalette === undefined) {
    defaultPalette = palettes.find((p) => p.isDefault);
  }
  // Sequential might not have a default, use the first
  if (defaultPalette === undefined && defaultPaletteType === OrgTypes.ColorPaletteType.Sequential) {
    defaultPalette = palettes[0];
  }
  return defaultPalette;
}

export function getMarkColorValue(encoding: Encoding, field: EncField) {
  if (encoding.marks.channel === '*') {
    return encoding.marks.properties.color.value;
  }
  const series = encoding.marks.series.find((s) => s.properties.color.values[0]?.id === field.id);
  return series?.properties.color.value;
}
